import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { BiodiversityZoneChart } from '@/components/Charts';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';

import { NoPlotData } from '../NoPlotData';

export const BiodiversityGraphTile = ({
  data,
  benchmarks,
  highBenchmark,
  moderateBenchmark,
  lowBenchmark,
  activeCustomBenchmark,
  selectedBenchmarkId,
  onBenchmarkSelect,
}: {
  data: GraphFact[] | null | undefined;
  benchmarks: number[];
  highBenchmark: PlotReportFactElement<GraphFact[] | null> | undefined;
  moderateBenchmark: PlotReportFactElement<GraphFact[] | null> | undefined;
  lowBenchmark: PlotReportFactElement<GraphFact[] | null> | undefined;
  activeCustomBenchmark?: CustomBenchmarkSuccessResponse | null;
  selectedBenchmarkId: string | null;
  onBenchmarkSelect: (id: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { zoom, setZoom } = useChartZoom();

  const bpLowBenchmarkData = (lowBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_low` };
  });

  const bpModerateBenchmarkData = (moderateBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_moderate` };
  });

  const bpHighBenchmarkData = (highBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_high` };
  });

  /**
   * We only null check for bpHistoricData since bpHistoricData is the primary source data of the graph
   * reference lines are displayed only when bpHistoricData is rendered
   */
  const noChartData = !data?.length;

  if (noChartData) {
    return <NoPlotData />;
  }

  const chartData = [...data, ...bpLowBenchmarkData, ...bpModerateBenchmarkData, ...bpHighBenchmarkData];

  return (
    <Stack
      spacing={6}
      className='items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'
      data-testid='biodiversity-graph-tile'
    >
      <ChartHeading
        heading={t('shared.ncaDetail.details.biodiversity.labels.historicalGraphHeadline')}
        popover={{
          title: t('shared.ncaDetail.details.biodiversity.historicalData.title'),
          body: t('shared.ncaDetail.details.biodiversity.historicalData.body'),
        }}
        className='w-full px-6 sm:px-0'
        zoom={zoom}
        setZoom={setZoom}
        indicator={GraphTileType.protected_on_farm_habitat}
        selectedBenchmark={selectedBenchmarkId}
        setSelectedBenchmark={onBenchmarkSelect}
      />
      <BiodiversityZoneChart
        data={chartData}
        benchmarks={benchmarks}
        customBenchmark={activeCustomBenchmark}
        showTooltip={true}
        className='relative'
        data-testid='biodiversity-zone-chart'
        zoom={zoom}
        setZoom={setZoom}
      />
    </Stack>
  );
};
